import Icon from "@components/Icon";
import {
	colours,
	fonts,
	fontsLineHeight,
	getColours,
	richTextHelper,
} from "@helpers";
import { Actioner, BuyFlowActionKeys } from "@services/actioner";
import Image from "next/image";
import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import styled, { type CSSObject } from "styled-components";
import backGroundImage from "../../public/images/Background.png";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import useDeviceDetect from "@helpers/deviceDetect";
import {
	mixpanelSetUserProperties,
	mixpanelTrack,
} from "@helpers/mixpanel.helper";
import TransformStringWithModal from "@helpers/transforStringWithModal";
import type { IPlanCarouselFields } from "@types-contentful";
import CTAButton from "./CTAButton";
import PromoCTAButton from "./PromoCtaButton";
import { usePathname } from "next/navigation";

type PlanCarouselPlan = NonNullable<
	IPlanCarouselFields["individualPlans"]
>[number];

const Container = styled.div<{
	id: string;
	resetModuleMarginBottom: "No" | "Yes" | undefined;
	backgroundColour: string | undefined;
	backgroundImage?: string | undefined;
	nonePlanPage: boolean;
}>`
  ${({ resetModuleMarginBottom }) =>
		resetModuleMarginBottom === "Yes" && "margin-bottom: 0px !important"};
  background-color: ${(props) => getColours(props.backgroundColour)};
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  z-index: 0;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

	padding: ${(props) => (props.nonePlanPage ? "40px 10%" : "0px")};
	@media (min-width: 834px) {
		padding: ${(props) => (props.nonePlanPage ? "40px 10%" : "0px")};
  }
  .slick-track {
    display: flex !important;
    justify-content: center;
    margin-bottom: 40px;
  }
  .slick-track {
    display: flex !important;
    height: 100%;
  }

  .slick-slide {
    height: auto;
  }
  .slick-slide > div {
    height: 100%;
    width: 100%;
  }

`;

const MainPromo = styled.div<{
	promoBackgroundColour:
		| "Peach"
		| "Midnight Blue"
		| "Pale Peach"
		| "White"
		| "Watermelon"
		| "Mint Green"
		| "Green"
		| undefined;
	adjustMargin: boolean;
}>`
  border-radius: 12px;
  background-color: ${(props) => getColours(props.promoBackgroundColour)};
  width: 304px;
  min-height: 56px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  gap: 27px;
  padding: 4px 14px;
  z-index: 1;
  font-family: var(--euclid-medium);
  @media (min-width: 1166px) {
    width: 1136px;
    gap: 16px;
    margin-bottom: ${(props) => (props.adjustMargin ? "32px" : "53px")};
	margin-top: 30px;
  }
`;
const Spacer = styled.div`
  width: 304px;

  @media (min-width: 1166px) {
    min-height: 56px;
    width: 1136px;
  }
`;

const CardsContainer = styled.div<{ allNull?: boolean }>`
  margin-top: 32px;
  display: flex;
  gap: 16px;
  justify-content: center;

  width: 1136px;

  @media (max-width: 1165px) {
    display: none;
  }
	
  .slick-slide > div > div {
    height: ${(props) => (props.allNull ? "100%" : "calc(100% - 40px)")};
  }
`;

const DesktopCarouselContainer = styled.div<{ allNull?: boolean }>`
	position: relative;
	width: 100%;
	max-width: 1328px;
	margin-top: 32px;

	@media (max-width: 1165px) {
		display: none;
	}
`;

const DesktopCarouselLayout = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 16px;
	width: 100%;
`;

const DesktopCarouselViewport = styled.div<{ allNull?: boolean }>`
	flex: 0 1 1184px;
	max-width: 1184px;
	width: 100%;
	overflow-x: hidden;
	padding-top: ${(props) => (props.allNull ? "0px" : "61px")};
	padding-bottom: 16px;
	box-sizing: border-box;
`;

const DesktopCarouselTrack = styled.div<{ offset: number }>`
	display: flex;
	gap: 16px;
	align-items: stretch;
	width: max-content;
	transform: translateX(${(props) => `-${props.offset}px`});
	transition: transform 300ms ease;
	will-change: transform;
`;

const DesktopCarouselSlide = styled.div`
	display: flex;
	flex: 0 0 368px;
	width: 368px;

	.card {
		width: 100%;
		height: 100%;
	}
`;

const DesktopCarouselArrow = styled.button<{ direction: "left" | "right" }>`
	all: unset;
	appearance: none;
	-webkit-appearance: none;
	box-sizing: border-box;
	flex: 0 0 48px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	background: transparent;
	border: none;
	outline: none;
	box-shadow: none;
	color: ${colours.MIDNIGHT_BLUE};
	cursor: pointer;
	transition: none;
	transform: none;

	&:hover,
	&:hover:enabled,
	&:focus,
	&:focus-visible,
	&:active {
		background: transparent !important;
		border: none !important;
		color: ${colours.MIDNIGHT_BLUE};
		outline: none !important;
		box-shadow: none !important;
		transform: none;
	}

	&:disabled {
		opacity: 0.35;
		cursor: default;
		pointer-events: none;
	}
`;

const CarouselCardContainer = styled.div<{ allNull?: boolean }>`
  .slick-list {
    padding: 0px;
  }
  .slick-slider {
    width: 400px;
  }
	.slick-track {
		display: flex !important;
		align-items: stretch;
	}
  .slick-slide {
    border-radius: 12px;
    margin: 0 8px;
    display: flex;
    flex-direction: column;
		justify-content: stretch;
		align-items: stretch;
		height: auto;
	}
	.slick-slide > div {
		height: 100%;
  }
  .slick-track {
    margin-top: ${(props) => (props.allNull ? "0px" : "61px")};
  }
`;

const MobileCardDiv = styled.div`
  z-index: 2;
  height: 100%;
  display: flex;
	align-items: stretch;
`;

const Card = styled.div`
  display: flex;
  padding: 0px 23px;
  flex-direction: column;
  justify-content: flex-start;
  border-radius: 12px;

  background: #fff;
  position: relative;
  margin-top: 64px;

  @media (min-width: 1168px) {
    width: 368px;
    margin-top: 0px;
    padding: 0px;

    border: 2px solid ${colours.PEACH};
  }
`;
const MobileCard = styled.div`
  display: flex;
  /* padding: 0px 23px; */
  flex-direction: column;
  justify-content: flex-start;
  border-radius: 12px;
  z-index: 2;

  background: #fff;
  box-shadow: 0px 7px 20px 0px rgba(0, 0, 0, 0.1);
  position: relative;
  height: 100%;
	width: 100%;
`;

const InnerLayout = styled.div<{ nonePlanPage: boolean }>`
  display: flex;
  padding: 32px 20px;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  height: 100%;

  span {
    margin-top: auto;
  }
  @media (min-width: 1168px) {
    padding: 40px 0px 40px 0px;
    padding: ${(props) =>
			props.nonePlanPage ? "40px 0px 40px 0px" : "40px 0px 40px 0px"};
    gap: 32px;
  }

  hr {
    width: 256px;
    background-color: #e0e0e0;
    border: none;
    height: 2px;
    margin: 0;
  }
  @media (min-width: 1168px) {
    hr {
      width: 304px;
    }
  }
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  #month {
    display: flex;
    justify-content: center;
    align-items: baseline;
    margin-top: 8px;
    font-size: 24px;
  }
  @media (min-width: 1168px) {
    #month {
      font-size: 30px;
      margin-top: 4px;
    }
  }
`;

const PlanName = styled.h3`
    font-size: ${fonts.H3};
    line-height: ${fontsLineHeight.H3};
`;

const Price = styled.p<{ discounted: boolean }>`
  font-size: 24px;
	margin-bottom: 4px;

  .originalPrice {
    text-decoration: ${(props) => (props.discounted ? "line-through" : "none")};
    font-size: ${(props) => (props.discounted ? "22px" : "24px")};
	font-weight: 700;
  }

  @media (min-width: 1168px) {
    font-size: 30px;
    .originalPrice {
      font-size: ${(props) => (props.discounted ? "22px" : "30px")};
    }
  }

  .discountedPrice {
    font-size: 24px;
    color: ${colours.MIDNIGHT_BLUE};
	font-weight: 700;

    @media (min-width: 1168px) {
      font-size: 30px;
    }
  }
`;

const PostDiscountPrice = styled.p`
	color: ${colours.MIDNIGHT_BLUE};
	font-size: 14px;
	line-height: 1.3;
	text-align: center;
`;

const DiscountDurationCopy = styled.p`
	font-size: 14px;
	text-align: center;
`;

const FeatureContent = styled.div<{ fontBoldness: boolean }>`
  display: flex;
  flex-direction: column;
  #feature-name {
    font-family: ${(props) =>
			props.fontBoldness
				? "EuclidCircularA !important"
				: "EuclidCircularAMedium !important"};
  }
  h3 {
    font-family: EuclidCircularAMedium !important;
    margin-bottom: 8px;
  }
  #desktop {
    font-weight: ${(props) => (props.fontBoldness ? "none" : "bold")};

    font-size: 18px !important;
    /* margin-bottom: 8px; */
  }
  p:not(#feature-name) {
    font-size: 14px;
  }
`;

const Features = styled.div<{
	paddingForNoneProductPages: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: ${(props) => props.paddingForNoneProductPages && "0px 12px"};
  padding: 0px 23px;
  hr {
    width: 256px;
    background-color: #e0e0e0;
    border: none;
    height: 2px;
  }
  @media (min-width: 1165px) {
    hr {
      width: 304px;
    }
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const PromoLabel = styled.div<{
	promoTabBackgroundColour:
		| "Peach"
		| "Midnight Blue"
		| "Pale Peach"
		| "Watermelon"
		| "Mint Green"
		| "Green"
		| undefined;
}>`
  display: flex;
  height: 61px;
  padding: 8px 14px;
  justify-content: center;
  align-items: center;
  border-radius: 12px 12px 0px 0px;
  background: ${(props) => getColours(props.promoTabBackgroundColour || "Watermelon")};
  width: 326px;
  position: absolute;
  top: -61px;
  left: 5.3%;
  z-index: 999;

  box-sizing: border-box;
  @media (max-width: 1165px) {
    width: 260px;
    left: 6.6%;
  }
`;

const PromoLabelContent = styled.div<{
	promoTabFontColour: "Midnight Blue" | "White" | undefined;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 7px;
  font-weight: bold;

  span {
    color: ${(props) => getColours(props.promoTabFontColour || "Midnight Blue")};
  }
`;
const Terms = styled.div`
  margin-top: 8px;
  width: 307px;
  p,
  b {
    font-size: 14px;
  }
  @media (min-width: 1165px) {
    width: 928px;
    text-align: center;
    margin-top: 32px;
  }
`;

const SliderButtonDiv = styled.div`
  display: flex;
  flex-direction: row;
  background: #f5f5f5;
  border-radius: 12px;
  height: 57px;
  margin-bottom: 16px;
  z-index: 1;
  margin-top: 30px;

  @media (max-width: 475px) {
    width: 90%;
  }
`;

const SliderButton = styled.div<{ active: boolean }>`
  background: #f5f5f5;

  width: 104px !important;
  min-width: inherit;
  padding: 0 !important;
  border: 2px solid
    ${(props) => (props.active ? `${colours.MIDNIGHT_BLUE}` : "transparent")};
  background: ${(props) => props.active && `${colours.WHITE}`};
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  cursor: pointer;

  p {
    font-weight: 700;
  }

`;

const AppContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 224px;
  height: auto;
  background-color: ${colours.PEACH};
  border-radius: 12px;
  padding: 24px 16px;
  gap: 16px;
  margin-left: auto;
  margin-right: auto;
  margin-top: auto;

  & a {
    width: 170px;
    max-width: 100%;
    text-align: center;
    text-underline-offset: 4px;
  }
`;

const SliderPlanName = styled.p`
color: ${colours.MIDNIGHT_BLUE};
    font-size: 14px;
`;
const mainPromoIconSizesDesktop = {
	iconMobile: 40,
	iconTablet: 40,
	iconLaptop: 40,
	iconDesktop: 40,
	iconDesktopLarge: 40,
};
const mainPromoIconSizes = {
	iconMobile: 23,
	iconTablet: 23,
	iconLaptop: 23,
	iconDesktop: 23,
	iconDesktopLarge: 23,
};
const featurePromoIconSizes = {
	iconMobile: 23,
	iconTablet: 23,
	iconLaptop: 23,
	iconDesktop: 23,
	iconDesktopLarge: 23,
};
const featuresIconSizes = {
	iconMobile: 24,
	iconTablet: 24,
	iconLaptop: 24,
	iconDesktop: 40,
	iconDesktopLarge: 40,
};
const featuresIconSizesNonePlanPages = {
	iconMobile: 24,
	iconTablet: 24,
	iconLaptop: 24,
	iconDesktop: 24,
	iconDesktopLarge: 24,
};
const desktopCarouselArrowIconSizes = {
	iconMobile: 30,
	iconTablet: 30,
	iconLaptop: 30,
	iconDesktop: 30,
	iconDesktopLarge: 30,
};

const DESKTOP_CARD_WIDTH = 368;
const DESKTOP_CARD_GAP = 16;
const DESKTOP_VISIBLE_PLAN_COUNT = 3;

const getPlanPromoLabel = (planName?: string) => {
	if (planName?.toLowerCase().includes("unlimited")) {
		return "Unlimited";
	}

	if (planName?.includes("50")) {
		return "50GB";
	}

	if (planName?.includes("25")) {
		return "25GB";
	}

	return planName;
};

const PlanCarousel = ({
	appStoreUrl,
	backgroundColour,
	googlePlayUrl,
	header,
	individualPlans,
	nonePlanPage,
	promoBackgroundColour,
	promoCopy,
	promoIconRef,
	removeNegativeSpacingBelowModule,
	tsAndCs,
	promoSubcopy,
	highlightedPlan,
}: IPlanCarouselFields) => {
	// @ts-ignore
	const planPromos = individualPlans?.map((i) => i.fields.planPromo);
	const allNull = planPromos?.every((item) => item === undefined);
	const resetModuleMarginBottom = removeNegativeSpacingBelowModule;
	const settings = {
		dots: false,
		infinite: false,
		speed: 500,
		slidesToShow: 1,
		slidesToScroll: 1,

		responsive: [
			{
				breakpoint: 1165,
				settings: {
					centerMode: true,
					centerPadding: "40px",
					slidesToShow: 1,
					slidesToScroll: 1,
					infinite: false,
					arrows: false,
				},
			},
		],
	};
	const [activeIndex, setActiveIndex] = useState(() => {
		if (highlightedPlan === "First") {
			return 0;
		}
		if (highlightedPlan === "Second") {
			return 1;
		}
		if (highlightedPlan === "Third") {
			return 2;
		}
		return 0;
	});
	const sliderRef = useRef<Slider | null>(null);
	const desktopCarouselViewportRef = useRef<HTMLDivElement | null>(null);
	const pathname = usePathname();
	const planCount = individualPlans?.length ?? 0;
	const [desktopSlideIndex, setDesktopSlideIndex] = useState(
		planCount === 4 ? 1 : 0,
	);
	const [desktopSlideOffset, setDesktopSlideOffset] = useState(0);

	const handleSlideChange = (index: number) => {
		setActiveIndex(index);

		sliderRef.current?.slickGoTo(index);
	};
	const handleBrazeEvent = (
		planName: string,
		planPrice: string,
		PlanDataInclusion: string,
	) => {
		Actioner.trigger(BuyFlowActionKeys.BUY_SET_PLAN, {
			plan_name: planName,
			data_inclusion: PlanDataInclusion,
			prices: planPrice,
		});
	};

	const { isAndroid, isIOS, isMobile, isTablet } = useDeviceDetect();
	const mobileAndTablet = isMobile || isTablet;

	const [isIOSPhone, setIsIOSPhone] = useState(false);
	const [isAndroidPhone, setIsAndroidPhone] = useState(false);

	useEffect(() => {
		setIsIOSPhone(isIOS);
		setIsAndroidPhone(isAndroid);
	}, [isAndroid, isIOS]);

	const [isScreenSmall, setIsScreenSmall] = useState(true);

	useEffect(() => {
		const updateIsScreenSmall = () => {
			setIsScreenSmall(window.innerWidth <= 1165);
		};

		if (typeof window !== "undefined") {
			updateIsScreenSmall();
			window.addEventListener("resize", updateIsScreenSmall);

			return () => window.removeEventListener("resize", updateIsScreenSmall);
		}
	}, []);
	const hasDesktopCarousel = !isScreenSmall && planCount === 4;

	useEffect(() => {
		setDesktopSlideIndex(planCount === 4 ? 1 : 0);
	}, [planCount]);

	useEffect(() => {
		if (!hasDesktopCarousel) {
			setDesktopSlideOffset(0);
			return;
		}

		const viewport = desktopCarouselViewportRef.current;

		if (!viewport) {
			return;
		}

		const updateDesktopSlideOffset = () => {
			const desktopTrackWidth =
				planCount * DESKTOP_CARD_WIDTH + (planCount - 1) * DESKTOP_CARD_GAP;
			setDesktopSlideOffset(
				Math.max(desktopTrackWidth - viewport.clientWidth, 0),
			);
		};

		updateDesktopSlideOffset();

		if (typeof ResizeObserver !== "undefined") {
			const resizeObserver = new ResizeObserver(updateDesktopSlideOffset);
			resizeObserver.observe(viewport);

			return () => resizeObserver.disconnect();
		}

		window.addEventListener("resize", updateDesktopSlideOffset);

		return () => window.removeEventListener("resize", updateDesktopSlideOffset);
	}, [hasDesktopCarousel, planCount]);

	const createHandleButtonClick = (plan) => () => {
		handleBrazeEvent(plan.planName, plan.planPrice, plan.planName);
	};

	const buildBuyUrl = (base: string, plan) => {
		const url = `${base}?id=${plan?.fields?.ctaButtonUrl}endid`;
		return `${url}&rc=${plan?.fields?.promoCodeForThisPlan}endrc`;
	};

	const planPageTheme = nonePlanPage === "Yes";

	const [isMounted, setIsMounted] = useState(false);

	useEffect(() => {
		setIsMounted(true);
	}, []);

	const renderPlanPrice = (plan, tabIndex?: number) => {
		const hasDiscount = Boolean(plan?.fields?.discountedPrice);
		const priceProps = typeof tabIndex === "number" ? { tabIndex } : {};

		return (
			<>
				<Price id="month" discounted={hasDiscount} {...priceProps}>
					{hasDiscount ? (
						<>
							<span
								className="discountedPrice"
								data-testid={`discountedPrice-${plan?.fields?.planName}`}
							>
								${plan?.fields?.discountedPrice}
							</span>
							<span style={{ fontSize: "18px" }}>/month</span>
						</>
					) : (
						<>
							<span className="originalPrice" data-testid="originalPrice">
								${plan?.fields?.planPrice}
							</span>
							<span style={{ fontSize: "18px" }}>/month</span>
						</>
					)}
				</Price>
				{hasDiscount && (
					<>
						<DiscountDurationCopy
							data-testid={`copyUnderDiscountedPrice-${plan?.fields?.planName}`}
							{...priceProps}
						>
							{plan?.fields?.copyUnderDiscountedPrice}, then ${plan?.fields?.planPrice}
						</DiscountDurationCopy>
						<PostDiscountPrice {...priceProps}>
							
						</PostDiscountPrice>
					</>
				)}
			</>
		);
	};

	const renderDesktopPlanCard = (plan: PlanCarouselPlan) => (
		<Card key={plan?.sys?.id} className="card">
			{plan?.fields?.planPromo && (
				<PromoLabel
					data-testid={`promo-label-${getPlanPromoLabel(plan?.fields?.planName)}`}
					id={plan.sys.id}
					promoTabBackgroundColour={plan.fields?.promoTabBackgroundColour}
				>
					<PromoLabelContent
						data-testid={`promo-label-content-${getPlanPromoLabel(plan?.fields?.planName)}`}
						promoTabFontColour={plan.fields?.promoTabFontColour}
					>
						{plan.fields?.planPromoIconRef !== undefined && (
							<Icon
								iconName={plan?.fields?.planPromoIconRef}
								iconSizes={featurePromoIconSizes}
								alt="promo icon"
							/>
						)}
						<span>{plan.fields?.planPromo}</span>
					</PromoLabelContent>
				</PromoLabel>
			)}
			<InnerLayout nonePlanPage={nonePlanPage === "Yes"}>
				<Heading>
					<PlanName>{plan?.fields?.planName}</PlanName>
					<p>{plan?.fields?.planNameSmallCopy}</p>
					{renderPlanPrice(plan)}
				</Heading>
				{nonePlanPage === "No" && <hr />}

				<Features paddingForNoneProductPages={nonePlanPage === "Yes"}>
					{plan?.fields?.items?.map((item) => {
						return (
							<React.Fragment key={item.sys.id}>
								<FeatureItem>
									{item?.fields?.iconRef && (
										<Icon
											iconName={item?.fields?.iconRef}
											iconSizes={
												!planPageTheme
													? featuresIconSizes
													: featuresIconSizesNonePlanPages
											}
											alt=""
										/>
									)}
									<FeatureContent fontBoldness={planPageTheme}>
										{item?.fields?.text === null ? (
											<p id="desktop">{item.fields?.header}</p>
										) : (
											<span id="desktop">
												{item?.fields?.header && (
													<TransformStringWithModal
														original={item?.fields?.header}
														textForLightbox={item?.fields?.textForLightbox}
														lightboxContent={item?.fields?.lightboxContent}
														headerFontSize={"large"}
													/>
												)}
											</span>
										)}
										{!planPageTheme && item.fields?.text && (
											<TransformStringWithModal
												original={item.fields?.text?.content[0]?.content[0]}
												textForLightbox={item?.fields?.textForLightbox}
												lightboxContent={item.fields?.lightboxContent}
											/>
										)}
									</FeatureContent>
								</FeatureItem>
								{!planPageTheme && <hr />}
							</React.Fragment>
						);
					})}
				</Features>
				<span
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: "16px",
						width: "100%",
					}}
				>
					{plan?.fields?.ctaButtonLabel && (
						<PromoCTAButton
							plan={plan}
							createHandleButtonClick={createHandleButtonClick}
						/>
					)}

					{(plan?.fields?.relativeUrl || plan?.fields?.linkUrl) &&
						plan?.fields?.linkText && (
							<a href={plan?.fields?.relativeUrl || plan?.fields?.linkUrl}>
								{plan?.fields?.linkText}
							</a>
						)}
				</span>
			</InnerLayout>
		</Card>
	);

	return (
		<Container
			id="plan-selector"
			resetModuleMarginBottom={resetModuleMarginBottom}
			backgroundColour={backgroundColour}
			nonePlanPage={nonePlanPage === "Yes"}
			backgroundImage={
				nonePlanPage === "No" ? "/Images/Background.png" : undefined
			}
			style={{
				backgroundImage:
					nonePlanPage === "No" ? `url(${backGroundImage.src})` : undefined,
				width: "100%",
				height: "100%",
				backgroundRepeat: "no-repeat",
				backgroundPosition: "top center",
				backgroundSize: "100% 485px",
				zIndex: 0,
			}}
			data-testid={`plan-carousel-${pathname || ""}`
				.toString()
				.toLowerCase()
				.replace(/\s+/g, "-")}
		>
			{header && <h2 style={{ marginBottom: "16px" }}>{header}</h2>}
			{!isScreenSmall && promoCopy && (
				<MainPromo
					data-testid={`main-promo-desktop/${(pathname || "")
						.toString()
						.toLowerCase()
						.replace(/^\//, "")
						.replace(/\s+/g, "-")}`}
					promoBackgroundColour={promoBackgroundColour}
					adjustMargin={Boolean(allNull)}
				>
					{" "}
					{promoIconRef && (
						<Icon
							iconName={promoIconRef}
							iconSizes={mainPromoIconSizesDesktop}
							alt="promo icon"
						/>
					)}
					{promoCopy}
				</MainPromo>
			)}
			{!promoCopy && <Spacer data-testid="promo-spacer" />}
			{promoSubcopy && !isScreenSmall && (
				<p style={{ marginBottom: "54px", zIndex: 99 }}>{promoSubcopy}</p>
			)}
			{hasDesktopCarousel ? (
				<DesktopCarouselContainer allNull={allNull}>
					<DesktopCarouselLayout>
						<DesktopCarouselArrow
							type="button"
							direction="left"
							disabled={desktopSlideIndex === 0}
							onClick={() => setDesktopSlideIndex(0)}
							aria-label="Show previous plans"
						>
							<Icon
								iconName="chevron-left"
								iconSizes={desktopCarouselArrowIconSizes}
								style={{ cursor: "pointer" }}
								alt="Show previous plans"
							/>
						</DesktopCarouselArrow>
						<DesktopCarouselViewport
							ref={desktopCarouselViewportRef}
							allNull={allNull}
						>
							<DesktopCarouselTrack
								offset={desktopSlideIndex === 0 ? 0 : desktopSlideOffset}
							>
								{individualPlans?.map((plan) => (
									<DesktopCarouselSlide key={plan?.sys?.id}>
										{renderDesktopPlanCard(plan)}
									</DesktopCarouselSlide>
								))}
							</DesktopCarouselTrack>
						</DesktopCarouselViewport>
						<DesktopCarouselArrow
							type="button"
							direction="right"
							disabled={
								desktopSlideIndex === planCount - DESKTOP_VISIBLE_PLAN_COUNT
							}
							onClick={() =>
								setDesktopSlideIndex(planCount - DESKTOP_VISIBLE_PLAN_COUNT)
							}
							aria-label="Show next plans"
						>
							<Icon
								iconName="chevron-right"
								iconSizes={desktopCarouselArrowIconSizes}
								style={{ cursor: "pointer" }}
								alt="Show next plans"
							/>
						</DesktopCarouselArrow>
					</DesktopCarouselLayout>
				</DesktopCarouselContainer>
			) : (
				<CardsContainer allNull={allNull}>
					{individualPlans?.map((plan) => renderDesktopPlanCard(plan))}
				</CardsContainer>
			)}
			{isScreenSmall && (
				<>
					<SliderButtonDiv>
						{individualPlans?.map((plan, index) => (
							<SliderButton
								onClick={() => handleSlideChange(index)}
								active={index === activeIndex}
								key={plan.sys.id}
							>
								{/*
              // @ts-ignore*/}
								<SliderPlanName>{plan.fields?.planName}</SliderPlanName>

								{/*
              // @ts-ignore*/}
								<Price>${plan.fields?.planPrice}</Price>
							</SliderButton>
						))}
					</SliderButtonDiv>
					{promoCopy && (
						/*@ts-ignore*/
						<MainPromo
							data-testid={`main-promo-mobile/${(pathname || "")
								.toString()
								.toLowerCase()
								.replace(/^\//, "")
								.replace(/\s+/g, "-")}`}
							promoBackgroundColour={promoBackgroundColour}
						>
							{" "}
							{promoIconRef && (
								<Icon
									iconName={promoIconRef}
									iconSizes={mainPromoIconSizes}
									alt="main promo icon"
								/>
							)}
							{promoCopy}
						</MainPromo>
					)}

					{promoSubcopy && (
						<p style={{ marginBottom: "24px", zIndex: 99, maxWidth: "304px" }}>
							{promoSubcopy}
						</p>
					)}
					<CarouselCardContainer allNull={allNull}>
						<Slider
							// @ts-ignore
							ref={sliderRef}
							afterChange={(newSlide) => handleSlideChange(newSlide)}
							initialSlide={activeIndex}
							{...settings}
						>
							{individualPlans?.map((plan, index) => {
								const isHidden = index !== activeIndex;
								return (
									<MobileCardDiv
										key={plan?.sys?.id}
										className="mobile-card-div"
										aria-hidden={isHidden}
									>
										{/* Adjust tabIndex for all focusable elements */}
										<MobileCard className="mobile-content-card">
											{plan.fields?.planPromo && (
												<PromoLabel
													data-testid={`promo-label-${plan?.fields?.planName?.toLowerCase().includes("unlimited") ? "Unlimited" : plan?.fields?.planName?.includes("50") ? "50GB" : plan?.fields?.planName?.includes("25") ? "25GB" : plan?.fields?.planName}`}
													id={plan.sys.id}
													tabIndex={isHidden ? -1 : 0}
													promoTabBackgroundColour={
														plan.fields?.promoTabBackgroundColour
													}
												>
													<PromoLabelContent
														data-testid={`promo-label-content-${plan?.fields?.planName?.toLowerCase().includes("unlimited") ? "Unlimited" : plan?.fields?.planName?.includes("50") ? "50GB" : plan?.fields?.planName?.includes("25") ? "25GB" : plan?.fields?.planName}`}
														promoTabFontColour={plan.fields?.promoTabFontColour}
													>
														{plan.fields?.planPromoIconRef && (
															<Icon
																iconName={plan.fields?.planPromoIconRef}
																alt={plan.fields?.planPromoIconRef}
															/>
														)}
														<span>{plan.fields.planPromo}</span>
													</PromoLabelContent>
												</PromoLabel>
											)}
											{/* @ts-ignore */}
											<InnerLayout>
												<Heading>
													<PlanName tabIndex={isHidden ? -1 : 0}>
														{plan.fields?.planName}
													</PlanName>
													<p>{plan?.fields?.planNameSmallCopy}</p>
													{renderPlanPrice(plan, isHidden ? -1 : 0)}
												</Heading>
												<Features paddingForNoneProductPages={planPageTheme}>
													{plan?.fields?.items?.map((item) => (
														<React.Fragment key={item.sys.id}>
															<FeatureItem tabIndex={isHidden ? -1 : 0}>
																{item?.fields?.iconRef && (
																	<Icon
																		iconName={item?.fields?.iconRef}
																		iconSizes={featuresIconSizes}
																		alt=""
																	/>
																)}
																<FeatureContent
																	fontBoldness={planPageTheme}
																	tabIndex={isHidden ? -1 : 0}
																>
																	<p id="feature-name">
																		{item?.fields?.header && (
																			<TransformStringWithModal
																				original={item?.fields?.header}
																				textForLightbox={
																					item?.fields?.textForLightbox
																				}
																				lightboxContent={
																					item?.fields?.lightboxContent
																				}
																				/* @ts-ignore */
																				tabIndex={isHidden ? -1 : 0}
																			/>
																		)}
																	</p>
																	{item?.fields?.text && (
																		<TransformStringWithModal
																			original={
																				item.fields.text?.content[0]?.content[0]
																			}
																			textForLightbox={
																				item?.fields?.textForLightbox
																			}
																			lightboxContent={
																				item.fields.lightboxContent
																			}
																			/* @ts-ignore */
																			tabIndex={isHidden ? -1 : 0}
																		/>
																	)}
																</FeatureContent>
															</FeatureItem>
															{!planPageTheme && <hr />}
														</React.Fragment>
													))}
												</Features>
												{plan?.fields?.ctaButtonLabel &&
													// react-device-detect reads the user agent, which the server can't
													// see, so the app-store variant is deferred until after mount.
													(isMounted && mobileAndTablet && !planPageTheme ? (
														<div>
																{(() => {
																	if (isMounted && isIOSPhone) {
																		return (
																			<AppContainer>
																				<p>Switch now via our app</p>
																				<a
																					href={`${appStoreUrl}`}
																					target="_blank"
																					rel="noreferrer"
																				>
																					<Image
																						alt="Get it on Appstore"
																						src="/images/AppStoreBadge.png"
																						width={162.5}
																						height={48}
																						data-gtm-cta-text="appleUrl"
																						data-gtm-cta-position="Plan Carousel Plan Page"
																						onClick={() =>
																							mixpanelTrack(
																								"download_app_clicked",
																								{
																									cta_position:
																										plan?.fields?.planName,
																									cta_text: "appleUrl",
																								},
																							)
																						}
																					/>
																				</a>
																				<a
																					href={buildBuyUrl(
																						"https://felixmobile.com.au/buy/",
																						plan,
																					)}
																					onClick={() =>
																						mixpanelTrack("text_link_clicked", {
																							cta_position:
																								plan?.fields?.planName,
																							cta_text: "Or sign up on web",
																						})
																					}
																				>
																					Or sign up on web
																				</a>
																			</AppContainer>
																		);
																	}

																	if (isAndroidPhone) {
																		return (
																			<AppContainer>
																				<p>Switch now via our app</p>
																				<a
																					href={`${googlePlayUrl}`}
																					target="_blank"
																					rel="noreferrer"
																					onClick={() =>
																						mixpanelTrack(
																							"download_app_clicked",
																							{
																								cta_position:
																									plan?.fields?.planName,
																								cta_text: "androidUrl",
																							},
																						)
																					}
																				>
																					<Image
																						alt="Get it on Google Play"
																						src="/images/PlayStoreBadge.png"
																						width={162.5}
																						height={48}
																						data-gtm-cta-text="androidUrl"
																						data-gtm-cta-position="Plan Carousel Plan Page"
																					/>
																				</a>
																				<a
																					href={buildBuyUrl(
																						"https://felixmobile.com.au/buy/",
																						plan,
																					)}
																					onClick={() =>
																						mixpanelTrack("text_link_clicked", {
																							cta_position:
																								plan?.fields?.planName,
																							cta_text: "Or sign up on web",
																						})
																					}
																				>
																					Or sign up on web
																				</a>
																			</AppContainer>
																		);
																	}

																	return null;
																})()}
															</div>
													) : (
														<span
															style={{
																display: "flex",
																flexDirection: "column",
																alignItems: "center",
																gap: "16px",
															}}
														>
															<CTAButton
																/* @ts-ignore */
																onClick={createHandleButtonClick(plan)}
																url={buildBuyUrl("/buy", plan)}
																ctaButtonColour={plan?.fields?.ctaButtonColour}
																/* @ts-ignore */
																gtmCtaText={plan?.fields?.ctaButtonLabel}
																gtmCtaPosition={plan?.fields?.planName}
																/* @ts-ignore */
																tabIndex={isHidden ? -1 : 0}
															>
																{/* @ts-ignore */}
																{plan?.fields?.ctaButtonLabel}
															</CTAButton>
															{(plan?.fields?.relativeUrl ||
																plan?.fields?.linkUrl) &&
																plan?.fields?.linkText && (
																	<a
																		href={
																			plan?.fields?.relativeUrl ||
																			plan?.fields?.linkUrl
																		}
																		tabIndex={isHidden ? -1 : 0}
																	>
																		{plan?.fields?.linkText}
																	</a>
																)}
														</span>
													))}
											</InnerLayout>
										</MobileCard>
									</MobileCardDiv>
								);
							})}
						</Slider>
					</CarouselCardContainer>
				</>
			)}

			<Terms data-testid="planCarouselTerms" style={{ zIndex: "9999" }}>
				{richTextHelper(tsAndCs)}
			</Terms>
		</Container>
	);
};

export default PlanCarousel;
